import { useEffect, useMemo, useState } from 'react'

interface UsePaginationOptions<T> {
  items: T[]
  pageSize: number
  resetDependencies?: any[]
}

interface UsePaginationReturn<T> {
  pageItems: T[]
  currentPage: number
  totalPages: number
  handlePageChange: (page: number) => void
}

export const usePagination = <T>({
  items,
  pageSize,
  resetDependencies = [],
}: UsePaginationOptions<T>): UsePaginationReturn<T> => {
  const [currentPage, setCurrentPage] = useState(1)

  const totalPages = useMemo(() => {
    if (items.length === 0) return 1
    return Math.ceil(items.length / pageSize)
  }, [items.length, pageSize])

  const pageItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return items.slice(startIndex, endIndex)
  }, [items, currentPage, pageSize])

  useEffect(() => {
    setCurrentPage(1)
  }, [items.length, ...resetDependencies])

  // Ensure current page is within valid range
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handlePageChange = (page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages))
    setCurrentPage(validPage)
  }

  return {
    pageItems,
    currentPage,
    totalPages,
    handlePageChange,
  }
}
